import {
  getGames,
  getGame,
  createGame,
  updateGame,
  deleteGame,
  startGame,
  endGame,
  getTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  getStages,
  createStage,
  updateStage,
  deleteStage,
  getMissions,
  createMission,
  updateMission,
  deleteMission,
  reorderStageMissions,
  createQuestion,
  updateQuestion,
  deleteQuestion
} from '@/api/outing-game'

const state = {
  games: [],
  game: null,
  teams: [],
  stages: [],
  missions: [],
  loading: false,
  error: null
}

const mutations = {
  SET_GAMES(state, games) {
    state.games = games
  },
  SET_CURRENT_GAME(state, game) {
    state.game = game
  },
  ADD_GAME(state, game) {
    state.games.push(game)
  },
  UPDATE_GAME(state, updatedGame) {
    const index = state.games.findIndex((g) => g._id === updatedGame._id)
    if (index !== -1) {
      state.games.splice(index, 1, updatedGame)
    }
    if (state.game && state.game._id === updatedGame._id) {
      state.game = updatedGame
    }
  },
  REMOVE_GAME(state, id) {
    state.games = state.games.filter((g) => g._id !== id)
    if (state.game && state.game._id === id) {
      state.game = null
    }
  },
  SET_TEAMS(state, teams) {
    state.teams = teams
  },
  SET_STAGES(state, stages) {
    state.stages = stages
  },
  ADD_STAGE(state, stage) {
    state.stages.push(stage)
  },
  UPDATE_STAGE(state, updatedStage) {
    const index = state.stages.findIndex((s) => s._id === updatedStage._id)
    if (index !== -1) {
      state.stages.splice(index, 1, updatedStage)
    }
  },
  REMOVE_STAGE(state, id) {
    state.stages = state.stages.filter((s) => s._id !== id)
  },
  SET_MISSIONS(state, missions) {
    state.missions = missions
  },
  ADD_MISSION(state, mission) {
    state.missions.push(mission)
  },
  UPDATE_MISSION(state, updatedMission) {
    const index = state.missions.findIndex((m) => m._id === updatedMission._id)
    if (index !== -1) {
      state.missions.splice(index, 1, updatedMission)
    }
  },
  REMOVE_MISSION(state, id) {
    state.missions = state.missions.filter((m) => m._id !== id)
  },
  SET_LOADING(state, status) {
    state.loading = status
  },
  SET_ERROR(state, error) {
    state.error = error
  }
}

const actions = {
  // ─── Game Actions ────────────────────────────────────────────────────────────

  async fetchGames({ commit }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await getGames()
      commit('SET_GAMES', response.data.games || response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch games')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchGame({ commit }, gameId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await getGame(gameId)
      commit('SET_CURRENT_GAME', response.data.game || response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch game')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createGame({ commit }, data) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await createGame(data)
      const game = response.data.game || response.data
      commit('ADD_GAME', game)
      return game
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create game')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateGame({ commit }, { gameId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await updateGame(gameId, data)
      const game = response.data.game || response.data
      commit('UPDATE_GAME', game)
      return game
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update game')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteGame({ commit }, gameId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      await deleteGame(gameId)
      commit('REMOVE_GAME', gameId)
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete game')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async startGame({ commit }, gameId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await startGame(gameId)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to start game')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async endGame({ commit }, gameId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await endGame(gameId)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to end game')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // ─── Team Actions ─────────────────────────────────────────────────────────────

  async fetchTeams({ commit }, gameId) {
    commit('SET_ERROR', null)
    try {
      const response = await getTeams(gameId)
      commit('SET_TEAMS', response.data || [])
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch teams')
      throw error
    }
  },

  async createTeam({ commit }, { gameId, data }) {
    commit('SET_ERROR', null)
    try {
      const response = await createTeam(gameId, data)
      return response.data.team || response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create team')
      throw error
    }
  },

  async updateTeam({ commit }, { gameId, teamId, data }) {
    commit('SET_ERROR', null)
    try {
      const response = await updateTeam(gameId, teamId, data)
      return response.data.team || response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update team')
      throw error
    }
  },

  async deleteTeam({ commit }, { gameId, teamId }) {
    commit('SET_ERROR', null)
    try {
      await deleteTeam(gameId, teamId)
      return true
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete team')
      throw error
    }
  },

  // ─── Stage Actions ────────────────────────────────────────────────────────────

  async fetchStages({ commit }, gameId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await getStages(gameId)
      commit('SET_STAGES', response.data || [])
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch stages')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createStage({ commit }, { gameId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await createStage(gameId, data)
      const stage = response.data.stage || response.data
      commit('ADD_STAGE', stage)
      return stage
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create stage')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateStage({ commit }, { gameId, stageId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await updateStage(gameId, stageId, data)
      const stage = response.data.stage || response.data
      commit('UPDATE_STAGE', stage)
      return stage
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update stage')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteStage({ commit }, { gameId, stageId }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      await deleteStage(gameId, stageId)
      commit('REMOVE_STAGE', stageId)
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete stage')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // ─── Mission Actions ──────────────────────────────────────────────────────────

  async fetchMissions({ commit }, gameId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await getMissions(gameId)
      commit('SET_MISSIONS', response.data || [])
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch missions')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createMission({ commit }, { gameId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await createMission(gameId, data)
      const mission = response.data.mission || response.data
      commit('ADD_MISSION', mission)
      return mission
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create mission')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateMission({ commit }, { gameId, missionId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await updateMission(gameId, missionId, data)
      const mission = response.data.mission || response.data
      commit('UPDATE_MISSION', mission)
      return mission
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update mission')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteMission({ commit }, { gameId, missionId }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      await deleteMission(gameId, missionId)
      commit('REMOVE_MISSION', missionId)
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete mission')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async reorderMissionsByStage({ commit }, { gameId, stageId, missionIds }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await reorderStageMissions(gameId, stageId, {
        missionIds
      })
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to reorder missions')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // ─── Question Actions ─────────────────────────────────────────────────────────

  async createQuestion({ commit }, { gameId, missionId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await createQuestion(gameId, missionId, data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create question')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateQuestion({ commit }, { gameId, missionId, questionId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await updateQuestion(gameId, missionId, questionId, data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update question')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteQuestion({ commit }, { gameId, missionId, questionId }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await deleteQuestion(gameId, missionId, questionId)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete question')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
